import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageCircle, RefreshCw, CheckCircle, AlertCircle, Clock,
  Search, ChevronLeft, ChevronRight, Loader, Zap
} from 'lucide-react';
import clsx from 'clsx';
import apiClient from '../services/api/client';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface Question {
  questionId:    string;
  marketplaceId: string;
  userCode:      string;
  itemId:        string;
  productTitle:  string;
  question:      string;
  aiAnswer:      string;
  status:        'answered' | 'answer_failed' | 'ai_failed' | 'pending';
  dateCreated:   string;
  processedAt:   string;
}

interface SyncResult {
  found:     number;
  enqueued:  number;
  error?:    string;
}

// ─── Config visual ────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  answered:      { label: 'Respondida',   color: 'bg-green-100 text-green-700',    icon: CheckCircle },
  answer_failed: { label: 'Falha envio',  color: 'bg-red-100 text-red-700',        icon: AlertCircle },
  ai_failed:     { label: 'Falha IA',     color: 'bg-orange-100 text-orange-700',  icon: AlertCircle },
  pending:       { label: 'Pendente',     color: 'bg-yellow-100 text-yellow-700',  icon: Clock },
};

const MP_CONFIG: Record<string, { label: string; color: string }> = {
  ml:          { label: 'Mercado Livre',  color: 'bg-yellow-100 text-yellow-800' },
  americanas:  { label: 'Americanas',     color: 'bg-red-100 text-red-800' },
  pontofrio:   { label: 'Ponto Frio',     color: 'bg-sky-100 text-sky-800' },
  extra:       { label: 'Extra',          color: 'bg-purple-100 text-purple-800' },
  casas:       { label: 'Casas Bahia',    color: 'bg-blue-100 text-blue-800' },
  magalu:      { label: 'Magalu',         color: 'bg-indigo-100 text-indigo-800' },
  netshoes:    { label: 'Netshoes',       color: 'bg-green-100 text-green-800' },
  shopee:      { label: 'Shopee',         color: 'bg-orange-100 text-orange-800' },
};

const ALL_MARKETPLACES = Object.keys(MP_CONFIG);

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Questions() {
  const [questions, setQuestions]     = useState<Question[]>([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [statusFilter, setStatus]     = useState('');
  const [mpFilter, setMpFilter]       = useState('');
  const [search, setSearch]           = useState('');
  const [loading, setLoading]         = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [syncResults, setSyncResults] = useState<Record<string, SyncResult> | null>(null);

  const limit = 20;

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { limit, page };
      if (statusFilter) params.status = statusFilter;
      if (mpFilter)     params.marketplace = mpFilter;
      const { data } = await apiClient.get('/questions', { params });
      setQuestions(data.questions || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Erro ao buscar perguntas', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, mpFilter]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const handleSync = async (marketplaces?: string[]) => {
    setSyncing(true);
    setSyncResults(null);
    try {
      const body = marketplaces ? { marketplaces, limit: 50 } : { limit: 50 };
      const { data } = await apiClient.post('/questions/sync', body);
      setSyncResults(data.results || {});
      setTimeout(() => { setSyncResults(null); fetchQuestions(); }, 5000);
    } catch (err: any) {
      console.error('Erro sync', err);
    } finally {
      setSyncing(false);
    }
  };

  const filtered = search
    ? questions.filter(q =>
        q.question.toLowerCase().includes(search.toLowerCase()) ||
        q.productTitle.toLowerCase().includes(search.toLowerCase())
      )
    : questions;

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <MessageCircle className="text-blue-600" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Perguntas</h1>
            <p className="text-sm text-gray-500">Respostas automáticas via IA — todos os marketplaces</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Sync por marketplace específico */}
          {ALL_MARKETPLACES.map(mp => (
            <button
              key={mp}
              onClick={() => handleSync([mp])}
              disabled={syncing}
              className={clsx(
                'text-xs px-2.5 py-1.5 rounded-lg font-medium transition border disabled:opacity-50',
                MP_CONFIG[mp]?.color || 'bg-gray-100 text-gray-700',
                'border-transparent hover:opacity-80'
              )}
            >
              {MP_CONFIG[mp]?.label || mp}
            </button>
          ))}

          {/* Sync todos */}
          <button
            onClick={() => handleSync()}
            disabled={syncing}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-60"
          >
            {syncing ? <Loader size={14} className="animate-spin" /> : <Zap size={14} />}
            {syncing ? 'Sincronizando...' : 'Todos'}
          </button>
        </div>
      </div>

      {/* Resultado do sync */}
      {syncResults && (
        <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Resultado da sincronização:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {Object.entries(syncResults).map(([mp, res]) => (
              <div key={mp} className={clsx(
                'rounded-lg px-3 py-2 text-xs',
                res.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              )}>
                <span className="font-semibold">{MP_CONFIG[mp]?.label || mp}: </span>
                {res.error
                  ? `❌ ${res.error}`
                  : `✅ ${res.found} encontradas, ${res.enqueued} enviadas`
                }
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por pergunta ou produto..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={mpFilter}
          onChange={e => { setMpFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos os marketplaces</option>
          {ALL_MARKETPLACES.map(mp => (
            <option key={mp} value={mp}>{MP_CONFIG[mp]?.label || mp}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Todos os status</option>
          <option value="answered">Respondidas</option>
          <option value="answer_failed">Falha no envio</option>
          <option value="ai_failed">Falha na IA</option>
          <option value="pending">Pendente</option>
        </select>

        <button
          onClick={fetchQuestions}
          className="flex items-center gap-2 border border-gray-300 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw size={14} className={clsx(loading && 'animate-spin')} />
          Atualizar
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-3">{total} pergunta{total !== 1 ? 's' : ''}</p>

      {/* Lista */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader size={32} className="animate-spin text-blue-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <MessageCircle size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-lg">Nenhuma pergunta encontrada</p>
          <p className="text-sm mt-1">Clique em um marketplace para sincronizar as perguntas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(q => <QuestionCard key={`${q.marketplaceId}-${q.questionId}`} q={q} onRefresh={fetchQuestions} />)}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm text-gray-600">Página {page} de {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Card de pergunta ─────────────────────────────────────────────────────────

function QuestionCard({ q, onRefresh }: { q: Question; onRefresh: () => void }) {
  const [expanded, setExpanded]   = useState(false);
  const [resending, setResending] = useState(false);

  const cfg  = STATUS_CONFIG[q.status] || STATUS_CONFIG.pending;
  const mpCfg = MP_CONFIG[q.marketplaceId] || { label: q.marketplaceId, color: 'bg-gray-100 text-gray-700' };
  const Icon = cfg.icon;

  const handleResend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setResending(true);
    try {
      await apiClient.post('/questions/sync', { marketplaces: [q.marketplaceId], limit: 1 });
      setTimeout(onRefresh, 1500);
    } catch (err) {
      console.error(err);
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition cursor-pointer"
      onClick={() => setExpanded(v => !v)}
    >
      {/* Linha topo */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={clsx('text-xs font-medium px-2 py-0.5 rounded-full', mpCfg.color)}>
              {mpCfg.label}
            </span>
            {q.productTitle && (
              <span className="text-xs text-gray-500 truncate max-w-xs">{q.productTitle}</span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-800 line-clamp-2">{q.question}</p>
        </div>
        <span className={clsx('flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap shrink-0', cfg.color)}>
          <Icon size={12} />
          {cfg.label}
        </span>
      </div>

      {/* Preview da resposta */}
      {!expanded && q.aiAnswer && (
        <p className="mt-2 text-xs text-gray-500 line-clamp-1 italic">💬 {q.aiAnswer}</p>
      )}

      {/* Expandido */}
      {expanded && (
        <div className="mt-4 space-y-3" onClick={e => e.stopPropagation()}>
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-xs font-semibold text-blue-600 mb-1">Resposta gerada pela IA</p>
            <p className="text-sm text-gray-700">{q.aiAnswer || '(sem resposta gerada)'}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
            <span>Item ID: <span className="font-mono">{q.itemId}</span></span>
            <span>Question ID: <span className="font-mono">{q.questionId}</span></span>
            <span>Recebida: {q.dateCreated ? new Date(q.dateCreated).toLocaleString('pt-BR') : '—'}</span>
            <span>Processada: {q.processedAt ? new Date(q.processedAt).toLocaleString('pt-BR') : '—'}</span>
          </div>

          {(q.status === 'answer_failed' || q.status === 'ai_failed') && (
            <button
              onClick={handleResend}
              disabled={resending}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-xs px-3 py-1.5 rounded-lg transition disabled:opacity-60"
            >
              {resending ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Retentar
            </button>
          )}
        </div>
      )}

      <div className="mt-2 text-xs text-gray-400 text-right">
        {q.processedAt ? new Date(q.processedAt).toLocaleDateString('pt-BR') : ''}
      </div>
    </div>
  );
}
